import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, TimeoutError } from 'rxjs';
import { catchError, timeout } from 'rxjs/operators';

import { AuthenticationService } from 'app/core/service';
import { isMobile } from './constants';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {

  constructor(private _router: Router, private _authenticationService: AuthenticationService, private route: ActivatedRoute) {
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const timeoutMs = 30000; // 30 seconds timeout
    // @ts-ignore
    return next.handle(request).pipe(
      timeout(timeoutMs),
      catchError(err => {
        if (err instanceof TimeoutError) {
          const timeoutError = {
            status: 408,
            messages: ['Request timeout. Please check your network connection and try again.']
          };
          return throwError(() => timeoutError);
        }
        if (!(err instanceof HttpErrorResponse)) {
          return throwError(() => err.error.message || err.statusText);
        }

        if (err.status === 401) {
          // try to use refresh token if 401 response returned from api
          return this.handle401Error(request, next);
        }

        if (err.status === 0) {
          // Server unreachable - logout to prevent state differences
          this.handleServerUnreachable();
          const networkError = {
            status: 0,
            messages: ['Server is unreachable. You have been logged out for security.']
          };
          return throwError(() => networkError);
        }

        if (err.status === 403) {
          this._router.navigate([`${this.route.firstChild?.snapshot.paramMap.get('subdomain')}/pages/miscellaneous/not-authorized`], {replaceUrl: true});
          return new Observable<HttpEvent<any>>();
        }

        // throwError
        const error = {
          status: err.status,
          messages: err.error.errors ? err.error.errors.map((key: any) => key.displayMessage) : [err.error.message || 'An error occurred'],
          // Preserve the original structured errors (code, completeMessage,
          // fieldId, etc.) so consumers that need field-level detail (e.g.
          // required-field validation errors) aren't limited to the
          // flattened `messages` array above.
          errors: err.error.errors
        };
        return throwError(() => error);
      })
    );
  }

  private handle401Error(_request: HttpRequest<any>, _next: HttpHandler) {
    this._authenticationService.refreshTokenExpired();
    return new Observable<HttpEvent<any>>();
  }

  private handleServerUnreachable() {
    // Clear user data to prevent state inconsistencies
    localStorage.removeItem('currentUserMybenefits');
    localStorage.removeItem('currentUserInfoMybenefits');
    localStorage.removeItem('currentUserCompanyInfoMybenefits');

    // Force page reload to reset app state instead of navigation
    if (isMobile()) {
      // For mobile apps, reload to company login
      this._router.navigate(['/default/pages/miscellaneous/no-connectivity']);
    } else {
      // For web, use logout service which handles navigation properly
      setTimeout(() => {
        this._authenticationService.refreshTokenExpired();
      }, 100);
    }
  }
}
